import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import IdleGuard from "@/components/IdleGuard";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're mid-OAuth-callback, skip /me — AuthCallback will exchange session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const endSession = useCallback(async (reason) => {
    sessionStorage.setItem("ar_signout_reason", reason);
    if (reason === "idle") { try { await api.post("/auth/logout"); } catch {} }
    setUser((u) => {
      if (u) window.location.assign(`/login?reason=${reason}`);
      return false;
    });
  }, []);
  const onIdle = useCallback(() => endSession("idle"), [endSession]);

  useEffect(() => {
    const h = (e) => endSession(e.detail);
    window.addEventListener("session-ended", h);
    return () => window.removeEventListener("session-ended", h);
  }, [endSession]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: load }}>
      {children}
      {user && <IdleGuard onTimeout={onIdle} />}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
